import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../api/axios";
import loadStatus from "../util/loadStatus";

export const getOrganizations = createAsyncThunk(
  "organizations/getOrganizations",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().organizations;
      if (state.count.getOrganizations <= 1) {
        const res = await axios.get("/nations", { params: reqData });
        return res.data;
      }
      return { data: state.data, count: state.total };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const getOrgProjectReports = createAsyncThunk(
  "projects/getOrgProjectReports",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().organizations;
      if (state.count.getOrgProjectReports <= 1) {
        const res = await axios.get("/project-reports");

        return res.data;
      }
      return { data: state.data, count: state.total };
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data.message);
      }

      return rejectWithValue(error.message);
    }
  }
);

export const addOrganizations = createAsyncThunk(
  "organizations/addOrganizations",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.post("/add-nation", reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const editOrganizations = createAsyncThunk(
  "organizations/editOrganizations",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.put("/edit-nation" + id, reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

export const deleteOrganizations = createAsyncThunk(
  "organizations/deleteOrganizations",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.delete("/delete-nation", reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error);
    }
  }
);

const organizationsSlice = createSlice({
  name: "organizations",
  initialState: {
    total: 0,
    data: [],
    selected: {},
    loading: false,
    error: null,
    load: {
      getOrganizations: false,
      getOrgProjectReports: false,
      getDonor: false,
      addOrganizations: false,
      editOrganizations: false,
      deleteOrganizations: false,
    },
    count: { getOrganizations: 0, getDonor: 0, getOrgProjectReports: 0 },
  },
  reducers: {
    setOrganizations: (state, action) => {
      state.data = action.payload;
    },
    setSelectedOrg: (state, action) => {
      state.selected = action.payload;
    },
    unsetOrganizations: (state) => {
      state.data = [];
      state.selected = {};
    },
  },
  extraReducers: (builder) => {
    builder
      //Get all Organizations
      .addCase(getOrganizations.pending, (state) => {
        state.load.getOrganizations = loadStatus.loading;
        state.error = null;
        state.count.getOrganizations = ++state.count.getOrganizations;
      })
      .addCase(getOrganizations.fulfilled, (state, action) => {
        state.load.getOrganizations = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getOrganizations = 0;
      })
      .addCase(getOrganizations.rejected, (state, action) => {
        state.load.getOrganizations = loadStatus.error;
        state.error = action.payload;
        state.count.getOrganizations = 0;
      })
      //Get all Projects Reports
      .addCase(getOrgProjectReports.pending, (state) => {
        state.load.getOrgProjectReports = loadStatus.loading;
        state.error = null;
        state.count.getOrgProjectReports = ++state.count.getOrgProjectReports;
      })
      .addCase(getOrgProjectReports.fulfilled, (state, action) => {
        state.load.getOrgProjectReports = loadStatus.success;
        state.data = action.payload.data;
        state.total = action.payload.count;
        state.count.getOrgProjectReports = 0;
      })
      .addCase(getOrgProjectReports.rejected, (state, action) => {
        state.load.getOrgProjectReports = loadStatus.error;
        state.error = action.error;
        state.count.getOrgProjectReports = 0;
      })
      //Add Organization
      .addCase(addOrganizations.pending, (state) => {
        state.load.addOrganizations = loadStatus.loading;
        state.error = null;
      })
      .addCase(addOrganizations.fulfilled, (state, action) => {
        state.load.addOrganizations = loadStatus.success;
        state.data.unshift(action.payload);
      })
      .addCase(addOrganizations.rejected, (state, action) => {
        state.load.addOrganizations = loadStatus.error;
        state.error = action.payload;
      })
      //Update Organization
      .addCase(editOrganizations.pending, (state) => {
        state.load.editOrganizations = loadStatus.loading;
        state.error = null;
      })
      .addCase(editOrganizations.fulfilled, (state, action) => {
        state.load.editOrganizations = loadStatus.success;
        const index = state.data.findIndex(
          (item) => (item.id = action.payload.id)
        );
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editOrganizations.rejected, (state, action) => {
        state.load.editOrganizations = loadStatus.error;
        state.error = action.payload;
      })
      //Delete Organization
      .addCase(deleteOrganizations.pending, (state) => {
        state.load.deleteOrganizations = loadStatus.loading;
        state.error = null;
      })
      .addCase(deleteOrganizations.fulfilled, (state, action) => {
        state.load.deleteOrganizations = loadStatus.success;
        state.data.filter((item) => item.id !== action.meta.arg.id);
      })
      .addCase(deleteOrganizations.rejected, (state, action) => {
        state.load.deleteOrganizations = loadStatus.error;
        state.error = action.payload;
      });
  },
});

export const { setOrganizations, setSelectedOrg, unsetOrganizations } =
  organizationsSlice.actions;
export default organizationsSlice.reducer;
